import React from 'react'
import { Checkbox, FormControlLabel } from '@mui/material'
import BrandedAlertModal from '../Modal/BrandedAlertModal/BrandedAlertModal'
import InstabookVideoIframe from '../InstabookVideoIframe/InstabookVideoIframe'

// T2-6043: All three Auto-book activation popups now use the shared
// BrandedAlertModal so they match the brand-design pattern (the same modal
// shell used by T2-5767, T2-5773, and the "I'll do it later" invoice prompt).
// Public component signatures are kept identical so existing callers work
// without changes.

const CalendarIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
)

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M5 12.5l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const PlayIllustration: React.FC = () => (
  <svg viewBox="0 0 64 64" fill="none" aria-hidden="true" focusable="false">
    <rect x="6" y="14" width="52" height="36" rx="6" stroke="currentColor" strokeWidth="2.5" />
    <path d="M28 24l12 8-12 8V24Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
)

const ChecklistIllustration: React.FC = () => (
  <svg viewBox="0 0 64 64" fill="none" aria-hidden="true" focusable="false">
    <rect x="10" y="8" width="44" height="48" rx="4" stroke="currentColor" strokeWidth="2.5" />
    <path d="M18 22h4M18 34h4M18 46h4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M28 22h22M28 34h22M28 46h16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
)

const SuccessIllustration: React.FC = () => (
  <svg viewBox="0 0 64 64" fill="none" aria-hidden="true" focusable="false">
    <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="3" />
    <path
      d="M20 32l8 8 16-16"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const InstaVideoModal: React.FC<{ open: boolean; url: string; onClose: () => void; onContinue: () => void }> = ({
  open,
  url,
  onClose,
  onContinue,
}) => (
  <BrandedAlertModal
    isOpen={open}
    onClose={onClose}
    headerLabel="Activate Auto-book"
    headerIcon={<CalendarIcon />}
    illustration={<PlayIllustration />}
    illustrationVariant="info"
    title="Activate Auto-book Scheduling?"
    ariaLabel="Activate Auto-book Scheduling"
    actions={[
      { label: 'Cancel', variant: 'secondary', onClick: onClose },
      { label: 'I Understand The Video', variant: 'primary', onClick: onContinue },
    ]}
  >
    <div style={{ marginBottom: 12 }}>
      This is a great feature for booking more shifts, but it&apos;s very important you understand how it
      works and follow the protocols. Please watch this short explainer video before activating Availability
      Scheduling.
    </div>
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <InstabookVideoIframe url={url} />
    </div>
  </BrandedAlertModal>
)

export const InstaTermsModal: React.FC<{
  open: boolean
  checks: { a: boolean; b: boolean; c: boolean }
  setChecks: (v: { a: boolean; b: boolean; c: boolean }) => void
  onAgree: () => void
  onClose: () => void
}> = ({ open, checks, setChecks, onAgree, onClose }) => {
  const allChecked = checks.a && checks.b && checks.c

  return (
    <BrandedAlertModal
      isOpen={open}
      onClose={onClose}
      headerLabel="Acknowledge & Agree"
      headerIcon={<CheckIcon />}
      illustration={<ChecklistIllustration />}
      illustrationVariant="info"
      title="Acknowledge and Agree to Activate Auto-book"
      ariaLabel="Acknowledge and Agree to Activate Auto-book"
      actions={[
        { label: 'Cancel', variant: 'secondary', onClick: onClose },
        {
          label: 'I Understand and Agree',
          variant: 'primary',
          onClick: onAgree,
          disabled: !allChecked,
        },
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checks.a}
              onChange={(_, v) => setChecks({ ...checks, a: v })}
              sx={{ paddingY: '4px' }}
            />
          }
          label="I will keep my Auto-book Availability Schedule Updated"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={checks.b}
              onChange={(_, v) => setChecks({ ...checks, b: v })}
              sx={{ paddingY: '4px' }}
            />
          }
          label="I will accept at least 75% of Auto-booking Notifications that match my specified availability"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={checks.c}
              onChange={(_, v) => setChecks({ ...checks, c: v })}
              sx={{ paddingY: '4px' }}
            />
          }
          label="I will keep my membership in good standing by keeping my booking commitments"
        />
      </div>
    </BrandedAlertModal>
  )
}

export const InstaSuccessModal: React.FC<{ open: boolean; onGoSettings: () => void; onClose: () => void; name?: string }> = ({
  open,
  onGoSettings,
  onClose,
  name,
}) => (
  <BrandedAlertModal
    isOpen={open}
    onClose={onClose}
    headerLabel="Auto-book Activated"
    headerIcon={<CheckIcon />}
    illustration={<SuccessIllustration />}
    illustrationVariant="success"
    title={`Great, ${name || ''} - You're all set!`.replace(/\s,/, ',')}
    ariaLabel="Auto-book Activated"
    actions={[
      { label: 'Not now', variant: 'secondary', onClick: onClose },
      { label: 'Go to Availability Settings', variant: 'primary', onClick: onGoSettings },
    ]}
  >
    Now you can access the Auto-book Availability Settings to get started. This is where you&apos;ll set your
    hourly rate, your maximum distance and minimum shift length for auto-booking. Go there now to set these?
  </BrandedAlertModal>
)
